import { z } from "zod";

import {
  createLegalOptionalStatementSchema,
  createLegalOptionalStringSchema,
  LEGAL_FORM_MESSAGES,
  LegalPersonalDetailsSchema,
} from "./shared";

/** § 356a BGB — mandatory declaration shown above the confirm button in withdrawal forms. */
export const WITHDRAWAL_DECLARATION_TEXT =
  "Hiermit erkläre ich den Widerruf des oben angegebenen Vertrags. Mir ist bewusst, dass die Wirksamkeit meines Widerrufs von verschiedenen Voraussetzungen abhängt und wir diese im Einzelfall prüfen werden.";

/** Statutory withdrawal period for distance contracts (§ 355 BGB). */
export const WITHDRAWAL_PERIOD_DAYS = 14;

/** Show withdrawal entry while the subscription is younger than this (grace for time zones). */
export const WITHDRAWAL_ENTRY_VISIBILITY_DAYS = 15;

const WITHDRAWAL_ENTRY_VISIBILITY_MS = WITHDRAWAL_ENTRY_VISIBILITY_DAYS * 24 * 60 * 60 * 1000;

/** Whether a subscription purchase is still inside the withdrawal entry window. */
export const isSubscriptionWithinWithdrawalWindow = (
  originalPurchaseDateMillis: number | null | undefined,
  nowMillis: number = Date.now(),
): boolean => {
  if (!originalPurchaseDateMillis) {
    return false;
  }

  const elapsedMs = nowMillis - originalPurchaseDateMillis;

  return elapsedMs >= 0 && elapsedMs < WITHDRAWAL_ENTRY_VISIBILITY_MS;
};

/** § 356a BGB — website entry control label (footer, global). */
export const WITHDRAWAL_ENTRY_BUTTON_LABEL = "Vertrag widerrufen";

/** § 356a BGB — confirmation step in the withdrawal flow. */
export const WITHDRAWAL_CONFIRM_BUTTON_LABEL = "Widerruf bestätigen";

export const WithdrawalAblefySubmitSchema = LegalPersonalDetailsSchema.extend({
  courseId: z.string().min(1, "Bitte wähle einen Kurs aus."),
  ablefyOrderId: createLegalOptionalStringSchema(),
  statement: createLegalOptionalStatementSchema(),
});

/** Authenticated Stripe-app path: name only (contract + email come from auth token). */
export const WithdrawalStripeAppSubmitSchema = z.object({
  firstName: z.string().min(1, LEGAL_FORM_MESSAGES.firstName),
  lastName: z.string().min(1, LEGAL_FORM_MESSAGES.lastName),
});

export type WithdrawalAblefyFormInput = z.input<typeof WithdrawalAblefySubmitSchema>;
export type WithdrawalAblefySubmitBody = z.output<typeof WithdrawalAblefySubmitSchema>;
export type WithdrawalStripeAppFormInput = z.input<typeof WithdrawalStripeAppSubmitSchema>;
export type WithdrawalStripeAppSubmitBody = z.output<typeof WithdrawalStripeAppSubmitSchema>;

export const WithdrawalRequestBodySchema = z.discriminatedUnion("path", [
  z.object({
    path: z.literal("stripe_app_subscription"),
    idToken: z.string().min(1),
    data: WithdrawalStripeAppSubmitSchema,
  }),
  z.object({
    path: z.literal("ablefy_course"),
    data: WithdrawalAblefySubmitSchema,
  }),
]);

export type WithdrawalRequestBody = z.infer<typeof WithdrawalRequestBodySchema>;

export const WithdrawalPathSchema = z.enum(["stripe_app_subscription", "ablefy_course"]);

export type WithdrawalPath = z.infer<typeof WithdrawalPathSchema>;

export const WITHDRAWAL_PATH_LABELS: Record<WithdrawalPath, string> = {
  stripe_app_subscription: "App-Abo (Stripe)",
  ablefy_course: "Online-Präventionskurs (ablefy)",
};

export const getWithdrawalPathLabel = (path: string): string => WITHDRAWAL_PATH_LABELS[path as WithdrawalPath] ?? path;
